import { AbstractCouponService } from '@abstract-services/coupon/1.0/coupon.abstract-service';
import * as ngCommonHttp from '@angular/common/http';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CouponsAutoApplyCouponIdPut$Params } from '@generated/tcea/fn/coupons/coupons-auto-apply-coupon-id-put';
import { CouponsAutoApplyGet$Params } from '@generated/tcea/fn/coupons/coupons-auto-apply-get';
import {
  AutoApplyCoupon,
  AutoApplyCouponsList,
  Coupon,
  PagedRedemptionsResponse,
  PaginatedCouponResponse,
  RedemptionSummary,
  WalletCoupon,
} from '@generated/tcea/models';
import { CouponsService as generatedCouponsService } from '@generated/tcea/services/coupons.service';
import { ApiConfiguration } from '@generated/tcea/api-configuration';
import { Observable, throwError } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class CouponService extends AbstractCouponService {
  constructor(
    private generatedCouponsService: generatedCouponsService,
    private http: HttpClient,
    private config: ApiConfiguration,
  ) {
    super();
  }

  /**
   * Gets all coupons for current brand context
   *
   * @param brandId [string]
   * @param params [object]
   * @returns observable of all stores
   */
  //   get(
  //     brandId: string,
  //     params: {
  //       page?: number;
  //       query: {
  //         archived?: boolean;
  //         availabilityStart?: string;
  //         availabilityEnd?: string;
  //         search?: string;
  //         enabled?: boolean;
  //         type?: string;
  //       };
  //       sort: {
  //         direction?: string;
  //         field?: string;
  //       };
  //     },
  //   ): Observable<{ data: Coupon[]; pages: number }> {
  //     const { query, page, sort } = params;
  //     const { archived, search, availabilityEnd, availabilityStart, enabled, type } = query;
  //     const filtersArr = [];

  //     if (availabilityStart) filtersArr.push(`availabilityStart:${availabilityStart} TO 9999-01-01`);
  //     if (availabilityEnd) filtersArr.push(`availabilityEnd:1111-01-01 TO ${availabilityEnd}`);
  //     if (enabled !== undefined) filtersArr.push(`enabled:${enabled}`);
  //     if (archived !== undefined) filtersArr.push(`archived:${archived}`);
  //     if (type !== undefined) filtersArr.push(`type:${type}`);

  //     return this.generatedCouponsService
  //       .couponsGet({
  //         'Active-Tenant-Identifier': brandId,
  //         filter: filtersArr,
  //         page,
  //         query: search,
  //         sortDirection: sort.direction ? [sort.direction] : undefined,
  //         sortField: sort.field ? [sort.field] : undefined,
  //         size: 100,
  //       })
  //       .pipe(
  //         map((response: PaginatedCouponResponse) => ({
  //           data: response._embedded.coupons || [],
  //           pages: response.totalPages || 0,
  //         })),
  //         catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
  //       );
  //   }

  private cache = new Map<string, Observable<{ data: Coupon[]; pages: number }>>();

  get(
    brandId: string,
    params: {
      page?: number;
      query: {
        archived?: boolean;
        availabilityStart?: string;
        availabilityEnd?: string;
        search?: string;
        enabled?: boolean;
        type?: string;
      };
      sort: {
        direction?: string;
        field?: string;
      };
    },
  ): Observable<{ data: Coupon[]; pages: number }> {
    const { query, page, sort } = params;
    const { archived, search, availabilityEnd, availabilityStart, enabled, type } = query;
    const filtersArr: string[] = [];

    if (availabilityStart) filtersArr.push(`availabilityStart:${availabilityStart} TO 9999-01-01`);
    if (availabilityEnd) filtersArr.push(`availabilityEnd:1111-01-01 TO ${availabilityEnd}`);
    if (enabled !== undefined) filtersArr.push(`enabled:${enabled}`);
    if (archived !== undefined) filtersArr.push(`archived:${archived}`);
    if (type !== undefined) filtersArr.push(`type:${type}`);

    // Generate stable cache key
    const cacheKey = JSON.stringify({
      brandId,
      filtersArr,
      page,
      search,
      sortDirection: sort.direction,
      sortField: sort.field,
    });

    // Use cached observable if available
    const cached$ = this.cache.get(cacheKey);
    if (cached$) {
      return cached$;
    }

    // Compose API call observable
    const request$ = this.generatedCouponsService
      .couponsGet({
        'Active-Tenant-Identifier': brandId,
        filter: filtersArr,
        page,
        query: search,
        sortDirection: sort.direction ? [sort.direction] : undefined,
        sortField: sort.field ? [sort.field] : undefined,
        size: 100,
      })
      .pipe(
        map((response: PaginatedCouponResponse) => ({
          data: response._embedded?.coupons || [],
          pages: response.totalPages || 0,
        })),
        catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(() => err)),
        shareReplay({ bufferSize: 1, refCount: false }), // cache and share across subscribers
      );

    this.cache.set(cacheKey, request$); // store observable in cache
    return request$;
  }

  /**
   * Gets all coupons for current brand context
   *
   * @param brandId [string]
   * @returns observable of all stores
   */
  getCouponById(couponId: string, brandId: string): Observable<Coupon> {
    return this.generatedCouponsService
      .couponsCouponIdGet({ 'Active-Tenant-Identifier': brandId, couponId })
      .pipe(
        map((response: any) => {
          return response;
        }),
        catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
      );
  }

  /**
   * Gets all coupons for current brand context
   *
   * @param brandId [string]
   * @returns observable of all stores
   */
  create(brandId: string, createData): Observable<Coupon> {
    let body = createData;
    body.tenant = brandId;
    return this.generatedCouponsService
      .couponsPost({ 'Active-Tenant-Identifier': brandId, body })
      .pipe(
        map((response: any) => {
          if (response) {
            this.cache.clear(); // clear cache on create
            return response;
          }
        }),
        catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
      );
  }

  /**
   * Gets all coupons for current brand context
   *
   * @param brandId [string]
   * @returns observable of all stores
   */
  update(brandId: string, couponId, updateData: Coupon): Observable<Coupon> {
    let body = updateData;
    return this.generatedCouponsService
      .couponsCouponIdPut({ 'Active-Tenant-Identifier': brandId, body, couponId })
      .pipe(
        map((response: any) => {
          this.cache.clear(); //
          return response;
        }),
        catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
      );
  }

  addCouponToWallet(brandId: string, coupon: WalletCoupon) {
    return this.generatedCouponsService
      .addCoupon({
        body: coupon,
        'Active-Tenant-Identifier': brandId,
      })
      .pipe(
        map((response: WalletCoupon) => {
          return response;
        }),
        catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
      );
  }

  getCouponFromWallet(brandId: string, couponId?: string) {
    return this.generatedCouponsService
      .getCoupons({
        'Active-Tenant-Identifier': brandId,
        managementCouponId: couponId,
      })
      .pipe(
        map((response: any) => {
          return response?._embedded?.coupon;
        }),
        catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
      );
  }

  /**
   * Gets all coupons for current brand context
   *
   * @param brandId [string]
   * @returns observable of all stores
   */
  autoApplyGet(brandId: string): Observable<AutoApplyCoupon[]> {
    let params: CouponsAutoApplyGet$Params = {
      'Active-Tenant-Identifier': brandId,
    };
    return this.generatedCouponsService.couponsAutoApplyGet(params).pipe(
      map((response: any) => {
        if (response.coupons) {
          return response.coupons;
        } else {
          return [];
        }
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  /**
   * Gets all coupons for current brand context
   *
   * @param brandId [string]
   * @returns observable of all stores
   */
  autoApplyPost(brandId: string, createData: AutoApplyCoupon[]): Observable<Coupon> {
    let body: AutoApplyCouponsList = {
      coupons: createData,
    };
    return this.generatedCouponsService
      .couponsAutoApplyPost({ 'Active-Tenant-Identifier': brandId, body })
      .pipe(
        map((response: any) => {
          if (response) {
            return response;
          }
        }),
        catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
      );
  }

  /**
   * Gets all coupons for current brand context
   *
   * @param brandId [string]
   * @returns observable of all stores
   */
  autoApplyPut(brandId: string, updateData): Observable<Coupon> {
    const params: CouponsAutoApplyCouponIdPut$Params = {
      'Active-Tenant-Identifier': brandId,
      couponId: updateData.couponId,
      body: { customerType: updateData.customerType },
    };
    return this.generatedCouponsService.couponsAutoApplyCouponIdPut(params).pipe(
      map((response: any) => {
        if (response) {
          return response;
        }
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  /**
   * Gets all coupons for current brand context
   *
   * @param brandId [string]
   * @returns observable of all stores
   */
  autoApplyDelete(brandId: string, autoApplyCouponId: string): Observable<Coupon> {
    return this.generatedCouponsService
      .couponsAutoApplyCouponIdDelete({
        'Active-Tenant-Identifier': brandId,
        couponId: autoApplyCouponId,
      })
      .pipe(
        map((response: any) => {
          if (response) {
            return response;
          }
        }),
        catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
      );
  }

  /**
   * Gets all redemptions for current brand context
   *
   * @param brandId [string]
   * @param params [object] - pagination parameters
   * @returns observable of paginated redemptions response
   */
  getRedemptions(
    brandId: string,
    params?: { page?: number; size?: number },
  ): Observable<PagedRedemptionsResponse> {
    return this.generatedCouponsService
      .getRedemptions({
        'Active-Tenant-Identifier': brandId,
        page: params?.page || 0,
        size: params?.size || 20,
      })
      .pipe(
        map((response: PagedRedemptionsResponse) => {
          return response;
        }),
        catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
      );
  }

  getRedemptionSummaries(
    brandId: string,
    params?: {
      page?: number;
      size?: number;
      periodEnd?: string;
      periodType?: 'DAY' | 'WEEK' | 'MONTH';
      filter?: Array<string>;
    },
  ): Observable<RedemptionSummary[]> {
    return this.generatedCouponsService
      .getRedemptionSummaries({
        'Active-Tenant-Identifier': brandId,
        page: params?.page || 0,
        size: params?.size || 20,
        periodEnd: params?.periodEnd,
        periodType: params?.periodType,
        filter: params?.filter,
      })
      .pipe(
        map((response: any) => {
          return response?.content || [];
        }),
        catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
      );
  }

  /**
   * Search catalog entries by PLU (POS IDs)
   *
   * @param brandId [string] - The brand identifier
   * @param plus [string[]] - Array of PLU values to search for
   * @returns observable of catalog entry relationships with catalogIds
   */
  getCatalogEntriesByPlu(
    brandId: string,
    plus: string[],
  ): Observable<{ catalogId: string; plu: string }[]> {
    let params = new HttpParams();
    params = params.set('Active-Tenant-Identifier', brandId);

    // Add each PLU as a separate query parameter
    plus.forEach((plu) => {
      params = params.append('plu', plu);
    });

    return this.http
      .get<any>(`${this.config.rootUrl}/catalog-entries/relationships`, {
        params,
      })
      .pipe(
        map((response: any) => {
          // Extract catalog IDs from the response
          // API returns { _embedded: { catalogEntries: [{ catalogEntryId, plu }] } }
          if (response && response._embedded && response._embedded.catalogEntries) {
            const mapped = response._embedded.catalogEntries.map((entry: any) => ({
              catalogId: entry.catalogEntryId,
              plu: entry.plu,
            }));
            return mapped;
          }
          return [];
        }),
        catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(() => err)),
      );
  }
}
